import type { AppManifest, AppRunDetail, AppRunMark } from '@api/apps';
import { SandboxRequestError } from '@components/HtmlSandboxViewer';
import type { KitMethods } from '../../../appKit/host';

/** Far above any instruction a person types, far below what the server's own cap allows. */
export const MAX_TEXT_CHARS = 20_000;
const MAX_ANSWERS = 50;
const MAX_MARKS = 200;
const MAX_POINTS = 2_000;

const invalid = (message: string): never => {
  throw new SandboxRequestError('INVALID', message);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Blob);

const objectOf = (value: unknown, what: string): Record<string, unknown> => {
  if (value === undefined) return {};
  return isPlainObject(value) ? value : invalid(`${what} must be an object.`);
};

const textOf = (value: unknown, what: string): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') return invalid(`${what} must be text.`);
  return value.length > MAX_TEXT_CHARS ? invalid(`${what} is longer than ${MAX_TEXT_CHARS} characters.`) : value;
};

const filesOf = (value: unknown, max: number): File[] => {
  if (value === undefined) return [];
  if (!Array.isArray(value) || !value.every((file) => file instanceof File)) return invalid('files must be a list of files.');
  return value.length > max ? invalid(`At most ${max} files can be sent at once.`) : value;
};

const extensionOf = (name: string): string => name.slice(name.lastIndexOf('.') + 1).toLowerCase();

/** Settings the manifest declares, by id; values are judged by the server against their declared types. */
const optionsOf = (value: unknown, manifest: AppManifest): Record<string, unknown> => {
  const options = objectOf(value, 'options');
  const declared = new Set(manifest.options.map((option) => option.id));
  const unknown = Object.keys(options).filter((id) => !declared.has(id));
  return unknown.length > 0 ? invalid(`'${unknown[0]}' is not a setting of this app.`) : options;
};

export const startParamsOf = (raw: unknown, manifest: AppManifest, run: AppRunDetail | null) => {
  const params = objectOf(raw, 'start') as Partial<Record<keyof KitMethods['start'], unknown>>;
  const files = filesOf(params.files, manifest.inputs.maxFiles);
  const accepted = new Set(manifest.inputs.acceptedExtensions.map((extension) => extension.toLowerCase()));
  const refused = files.find((file) => !accepted.has(extensionOf(file.name)));
  if (refused) invalid(`${refused.name}: this app does not take that kind of file.`);
  const ownInputs = new Set(run?.inputs.map((input) => input.attachmentId) ?? []);
  const keep = params.keepInputIds;
  if (keep !== undefined && (!Array.isArray(keep) || !keep.every((id) => typeof id === 'string' && ownInputs.has(id)))) {
    invalid('keepInputIds must name inputs of this run.');
  }
  return {
    prompt: textOf(params.prompt, 'prompt'),
    options: optionsOf(params.options, manifest),
    files,
    keepInputIds: keep as string[] | undefined,
    example: params.example === true,
  };
};

const marksOf = (value: unknown): AppRunMark[] | undefined => {
  if (value === undefined) return undefined;
  const valid =
    Array.isArray(value) &&
    value.length <= MAX_MARKS &&
    value.every(
      (mark) =>
        isPlainObject(mark) &&
        (mark.kind === 'box' || mark.kind === 'freehand') &&
        Array.isArray(mark.points) &&
        mark.points.length <= MAX_POINTS &&
        mark.points.every(
          (point: unknown) =>
            Array.isArray(point) && point.length === 2 && point.every((n) => typeof n === 'number' && Number.isFinite(n)),
        ),
    );
  return valid ? (value as AppRunMark[]) : invalid('marks must be boxes or strokes of [x, y] points.');
};

export const refineParamsOf = (raw: unknown) => {
  const params = objectOf(raw, 'refine') as Partial<Record<keyof KitMethods['refine'], unknown>>;
  const files = filesOf(params.files, 1);
  if (files.some((file) => !file.type.startsWith('image/'))) invalid('Only an image can go with a refinement.');
  const text = textOf(params.text, 'text');
  const marks = marksOf(params.marks);
  if (!text?.trim() && files.length === 0 && !marks?.length) invalid('Say what should change.');
  return { text, files, marks };
};

export const answerParamsOf = (raw: unknown) => {
  const params = objectOf(raw, 'answer') as Partial<Record<keyof KitMethods['answer'], unknown>>;
  const answers = params.answers;
  if (!Array.isArray(answers) || answers.length === 0 || answers.length > MAX_ANSWERS || !answers.every(isPlainObject)) {
    invalid('answers must be a list of answers.');
  }
  const files = filesOf(params.files, MAX_ANSWERS);
  if (files.some((file) => !file.type.startsWith('image/'))) invalid('Only images can go with answers.');
  return { answers: answers as Record<string, unknown>[], files };
};

export const fileIdOf = (raw: unknown): string => {
  const params = objectOf(raw, 'file');
  return typeof params.id === 'string' && params.id.length <= 200 ? params.id : invalid('id must be a file id.');
};

// An 8 MB picture, base64-encoded, with room for its data URL prefix.
const MAX_VIEW_CHARS = 11_300_000;
const MAX_VIEW_SIDE = 4096;
const MAX_NAME_CHARS = 200;

const nameOf = (value: unknown, what: string): string =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_NAME_CHARS ? value : invalid(`${what} must be named.`);

const sideOf = (value: unknown, what: string): number =>
  Number.isInteger(value) && (value as number) > 0 && (value as number) <= MAX_VIEW_SIDE
    ? (value as number)
    : invalid(`${what} must be between 1 and ${MAX_VIEW_SIDE} pixels.`);

export const viewRenderParamsOf = (raw: unknown): KitMethods['renderView'] => {
  const params = objectOf(raw, 'render');
  const { view } = params;
  if (typeof view !== 'string' || !view.startsWith('data:image/') || view.length > MAX_VIEW_CHARS) {
    return invalid('view must be an image of at most 8 MB.');
  }
  return {
    view,
    width: sideOf(params.width, 'width'),
    height: sideOf(params.height, 'height'),
    model: nameOf(params.model, 'model'),
    theme: nameOf(params.theme, 'theme'),
    // Kits built before this flag send none.
    background: params.background === true,
  };
};
